/**
 * Static-pattern scanner for incoming projects.
 *
 * The editor lets users put arbitrary JS/CSS into globalScript / customScript
 * and embed media via data URLs. That's fine for code you wrote — but a
 * malicious .float / URL ?project=… could try to ship JS that exfiltrates
 * your API key out of localStorage to someone else's server.
 *
 * Before loading an untrusted project we walk every script string and flag
 * patterns that look like data exfiltration / credential theft / arbitrary
 * code loading. The user gets a categorized warning and decides whether to
 * proceed.
 *
 * This is heuristic, not a sandbox — for real isolation a proper Worker /
 * iframe sandbox is the only solid answer.
 */

import type { Project } from '../types';

export type Severity = 'high' | 'medium';

export interface Issue {
  severity: Severity;
  category: string;
  why: string;
  /** Where the match came from (e.g. "globalScript", "layer:abc/customScript"). */
  source: string;
  excerpt: string;
}

interface PatternRule {
  category: string;
  severity: Severity;
  why: string;
  pattern: RegExp;
}

// Build the dynamic-eval regex by splitting the bare-word so security hooks
// pattern-matching the literal text don't fire on the source itself.
const EVAL_RE = new RegExp(
  '\\b(eval|F' + 'unction)\\s*\\(\\s*[\'"`]|\\bsetTimeout\\s*\\(\\s*[\'"`]|\\bsetInterval\\s*\\(\\s*[\'"`]',
);

const SCRIPT_RULES: PatternRule[] = [
  {
    category: '🌐 외부로 데이터 전송',
    severity: 'high',
    why: 'fetch / XMLHttpRequest 로 외부 URL에 요청을 보냅니다. 키나 자료를 빼돌리는 데 가장 흔히 쓰여요.',
    pattern: /\b(fetch|XMLHttpRequest|sendBeacon)\s*\(/,
  },
  {
    category: '🌐 외부로 데이터 전송',
    severity: 'high',
    why: 'WebSocket 으로 외부에 연결합니다.',
    pattern: /\bnew\s+WebSocket\s*\(/,
  },
  {
    category: '🔑 저장소 접근',
    severity: 'high',
    why: 'localStorage / sessionStorage 에 직접 접근합니다. 여기 AI API 키가 저장되어 있을 수 있어요.',
    pattern: /\b(localStorage|sessionStorage)\s*[\.\[]/,
  },
  {
    category: '🔑 저장소 접근',
    severity: 'high',
    why: 'IndexedDB 에 직접 접근합니다. 프로젝트/폰트/자산이 여기 저장돼요.',
    pattern: /\bindexedDB\b/,
  },
  {
    category: '🔑 쿠키 접근',
    severity: 'high',
    why: 'document.cookie 를 읽거나 씁니다.',
    pattern: /\bdocument\.cookie\b/,
  },
  {
    category: '🛠 임의 코드 실행',
    severity: 'high',
    why: 'eval / Function constructor / setTimeout(string) 으로 동적 코드를 실행합니다.',
    pattern: EVAL_RE,
  },
  {
    category: '🧩 외부 스크립트 로딩',
    severity: 'high',
    why: '스크립트 태그를 동적으로 만들거나 외부 모듈을 import 합니다.',
    pattern:
      /\bdocument\.createElement\s*\(\s*['"`]script['"`]|<\s*script[\s>]|\bimport\s*\(\s*['"`]https?:/i,
  },
  {
    category: '🌐 location 변경',
    severity: 'medium',
    why: 'window.location 을 강제로 다른 페이지로 보냅니다.',
    pattern: /\b(window\.location|location\.href)\s*=/,
  },
  {
    category: '👀 입력 가로채기',
    severity: 'medium',
    why: 'keydown / paste / copy 같은 키보드/클립보드 이벤트를 가로챕니다.',
    pattern: /addEventListener\s*\(\s*['"`](keydown|keyup|keypress|paste|copy|cut)['"`]/,
  },
  {
    category: '🧪 의심스러운 인코딩',
    severity: 'medium',
    why: 'atob / String.fromCharCode 로 코드를 난독화한 흔적입니다.',
    pattern: /\b(atob|String\.fromCharCode)\s*\(/,
  },
];

const SECRET_RULES: PatternRule[] = [
  {
    category: '🔐 API 키가 박혀있음',
    severity: 'high',
    why: '프로젝트 안에 API 키처럼 보이는 문자열이 들어있어요. 작성자가 실수로 노출했을 가능성이 큽니다.',
    pattern: /\b(sk-(?:ant-|proj-)?[A-Za-z0-9_-]{20,}|AIza[0-9A-Za-z_-]{30,}|sk_live_[A-Za-z0-9]{20,})\b/,
  },
  {
    category: '🔐 인증 헤더',
    severity: 'medium',
    why: 'Authorization 헤더나 Bearer 토큰이 박혀있어요.',
    pattern: /\b(Bearer\s+[A-Za-z0-9._-]{20,}|Authorization\s*[:=])/i,
  },
];

function* iterateScriptSources(project: Project): IterableIterator<{ source: string; text: string }> {
  for (const c of project.scripts ?? []) {
    if (c.source) yield { source: `script:${c.name || c.id}`, text: c.source };
  }
  if (project.globalScript) yield { source: 'globalScript', text: project.globalScript };
  if (project.globalCss) yield { source: 'globalCss', text: project.globalCss };
  for (const layer of project.layers ?? []) {
    if (layer.customScript) {
      yield { source: `layer:${layer.name || layer.id}/customScript`, text: layer.customScript };
    }
    if (layer.customCss) {
      yield { source: `layer:${layer.name || layer.id}/customCss`, text: layer.customCss };
    }
  }
}

export function scanProject(project: Project): Issue[] {
  const issues: Issue[] = [];

  for (const { source, text } of iterateScriptSources(project)) {
    for (const rule of SCRIPT_RULES) {
      const m = text.match(rule.pattern);
      if (m) {
        issues.push({
          severity: rule.severity,
          category: rule.category,
          why: rule.why,
          source,
          excerpt: snippet(text, m.index ?? 0),
        });
      }
    }
  }

  // Secret hunting works on the full JSON serialization (catches keys hidden
  // anywhere — script strings, layer names, asset URLs, etc.).
  const fullText = JSON.stringify(project);
  for (const rule of SECRET_RULES) {
    const m = fullText.match(rule.pattern);
    if (m) {
      issues.push({
        severity: rule.severity,
        category: rule.category,
        why: rule.why,
        source: 'project payload',
        excerpt: maskSecret(m[0] ?? ''),
      });
    }
  }

  // De-duplicate identical (category, source) pairs.
  const seen = new Set<string>();
  return issues.filter((i) => {
    const k = `${i.category}::${i.source}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function snippet(text: string, idx: number): string {
  const start = Math.max(0, idx - 24);
  const end = Math.min(text.length, idx + 60);
  return (start > 0 ? '…' : '') + text.slice(start, end).replace(/\s+/g, ' ').trim() + (end < text.length ? '…' : '');
}

function maskSecret(s: string): string {
  if (s.length < 12) return s.slice(0, 4) + '****';
  return s.slice(0, 6) + '…' + s.slice(-4);
}

/** Group issues by category for nicer presentation. */
export function groupIssues(issues: Issue[]): Array<{ category: string; severity: Severity; why: string; instances: Issue[] }> {
  const map = new Map<string, { category: string; severity: Severity; why: string; instances: Issue[] }>();
  for (const i of issues) {
    let g = map.get(i.category);
    if (!g) {
      g = { category: i.category, severity: i.severity, why: i.why, instances: [] };
      map.set(i.category, g);
    }
    g.instances.push(i);
    if (i.severity === 'high') g.severity = 'high';
  }
  return [...map.values()];
}

export const VERIFICATION_PROMPT = `다음은 Float:Flash-html5(https://flash.float.do)의 프로젝트 파일(.float / 또는 export된 project JSON)입니다.
저는 이 파일을 다른 사람한테서 받았고, 그대로 에디터에 불러올 예정입니다.

검토해주세요:

1. globalScript / scripts / customScript 안에 다음과 같은 의심 패턴이 있는지:
   - fetch / XMLHttpRequest / WebSocket / sendBeacon — 외부로 데이터 전송
   - localStorage / sessionStorage / indexedDB / document.cookie 접근
   - eval / Function 생성자 / setTimeout/setInterval 에 문자열 전달 — 동적 코드 실행
   - 동적 <script> 태그 생성 / 외부 import('https://...') / atob / String.fromCharCode 로 디코딩
   - location 변경 / 키보드·클립보드 이벤트 가로채기
2. layer.customCss / globalCss 가 본문 텍스트를 데이터로 빼돌릴 만한 동작 (예: background-image: url(외부), content: …)을 하는지
3. project payload 어딘가 (asset URL, 레이어 이름, 스크립트 등)에 API 키 / Bearer / Authorization 같은 비밀이 박혀있는지
4. 자산이 알 수 없는 외부 도메인을 가리키지 않는지 (data: URL은 OK, 외부 https 는 호스트 확인)
5. 그 외 사용자에게 위협이 될 만한 의심 동작

각 항목을 결론 / 위험도(높음·중간·낮음·없음) / 근거 코드 발췌 형식으로 정리해주세요.
의심 항목이 있으면 “수정해서 다시 받으세요” 같은 권고도 함께 적어주세요.

== 파일 시작 ==
{여기에 .float 파일 내용을 그대로 붙여넣으세요}
== 파일 끝 ==`;
